import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function JsonLd() {
  const location = useLocation();

  useEffect(() => {
    const existing = document.querySelector('script[data-jsonld]');
    if (existing) existing.remove();

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-jsonld', 'true');

    const schemas: object[] = [];

    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '开体育在线',
      url: 'https://www.kaitiyuonline.com',
      description: '开体育在线为您提供全面的体育赛事直播、赛程资讯、竞技数据与互动体验。',
      email: 'contact@kaitiyuonline.com',
      telephone: '400-888-6688',
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'CN',
        addressLocality: '北京',
        addressRegion: '朝阳区',
        streetAddress: '体育中心',
      },
    });

    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: '开体育在线',
      url: 'https://www.kaitiyuonline.com',
      potentialAction: {
        '@type': 'SearchAction',
        target: 'https://www.kaitiyuonline.com/search?q={search_term_string}',
        'query-input': 'required name=search_term_string',
      },
    });

    const path = location.pathname;

    if (path === '/' || path === '') {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: '开体育在线 - 体育赛事直播与竞技互动平台',
        description: '开体育在线为您提供全面的体育赛事直播、赛程资讯、竞技数据与互动体验。',
        url: 'https://www.kaitiyuonline.com/',
        isPartOf: { '@id': 'https://www.kaitiyuonline.com/#website' },
      });
    }

    if (path.startsWith('/category/')) {
      const slug = path.split('/category/')[1];
      const cat = categories.find((c) => c.slug === slug);
      if (cat) {
        schemas.push({
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: `${cat.name} - 开体育在线`,
          description: cat.description,
          url: `https://www.kaitiyuonline.com/category/${cat.slug}`,
          isPartOf: { '@id': 'https://www.kaitiyuonline.com/#website' },
        });
      }
    }

    if (path.startsWith('/article/')) {
      const id = path.split('/article/')[1];
      const article = articles.find((a) => a.id === id);
      if (article) {
        schemas.push({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.summary,
          author: { '@type': 'Person', name: article.author },
          datePublished: article.publishDate,
          url: `https://www.kaitiyuonline.com/article/${article.id}`,
        });
      }
    }

    if (path === '/about') {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: '关于我们 - 开体育在线',
        description: '了解开体育在线的品牌故事、平台优势与联系方式。',
        url: 'https://www.kaitiyuonline.com/about',
        isPartOf: { '@id': 'https://www.kaitiyuonline.com/#website' },
      });

      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: '开体育在线是什么？',
            acceptedAnswer: { '@type': 'Answer', text: '开体育在线是一个综合性体育赛事直播与资讯平台，提供足球、篮球、综合格斗、网球、电子竞技、赛车等热门项目的赛事直播、赛程资讯与竞技数据服务。' },
          },
          {
            '@type': 'Question',
            name: '开体育在线支持哪些体育项目？',
            acceptedAnswer: { '@type': 'Answer', text: '开体育在线覆盖足球、篮球、综合格斗、网球、电子竞技、赛车六大热门体育项目，每个项目都有专属的赛事直播、数据分析与深度资讯。' },
          },
          {
            '@type': 'Question',
            name: '如何在开体育在线观看赛事直播？',
            acceptedAnswer: { '@type': 'Answer', text: '您可以直接访问开体育在线官网，在首页即可看到正在直播的热门赛事。也可以通过分类导航选择您感兴趣的项目，查看所有正在直播和即将开始的赛程。' },
          },
          {
            '@type': 'Question',
            name: '开体育在线需要付费吗？',
            acceptedAnswer: { '@type': 'Answer', text: '开体育在线大部分赛事直播和资讯内容完全免费。部分高端赛事可能需要订阅，具体以平台实际公布为准。' },
          },
        ],
      });
    }

    if (path !== '/') {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: buildBreadcrumbItems(path),
      });
    }

    script.textContent = JSON.stringify(schemas.length === 1 ? schemas[0] : { '@context': 'https://schema.org', '@graph': schemas });
    document.head.appendChild(script);

    return () => {
      const el = document.querySelector('script[data-jsonld]');
      if (el) el.remove();
    };
  }, [location.pathname]);

  return null;
}

function buildBreadcrumbItems(path: string) {
  const segments = path.split('/').filter(Boolean);
  const items: object[] = [
    { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.kaitiyuonline.com/' },
  ];

  if (segments[0] === 'category') {
    items.push({ '@type': 'ListItem', position: 2, name: '赛事分类', item: 'https://www.kaitiyuonline.com/category' });
    if (segments[1]) {
      const cat = categories.find((c) => c.slug === segments[1]);
      items.push({ '@type': 'ListItem', position: 3, name: cat?.name || segments[1] });
    }
  } else if (segments[0] === 'article') {
    items.push({ '@type': 'ListItem', position: 2, name: '资讯详情' });
  } else if (segments[0] === 'about') {
    items.push({ '@type': 'ListItem', position: 2, name: '关于我们' });
  }

  return items;
}